import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useCart } from '../contexts/CartContext';
import { supabase } from '../services/supabase';
import { CustomerAddress, PickupPoint } from '../types';
import toast from 'react-hot-toast';
import {
    TruckIcon,
    BuildingStorefrontIcon,
    CreditCardIcon,
    ArrowLeftIcon,
    MapPinIcon,
} from '@heroicons/react/24/outline';
import {
    calculateDeliveryPriceFromDB,
    subscribeToDeliveryConfig,
    calculateDeliveryPriceSync,
} from '../utils/deliveryConfig';
import { createOrder } from '../services/orderService';
import AddAddressDialog from '../components/AddAddressDialog';

type DeliveryMethod = 'courier' | 'pickup';
type PaymentMethod = 'cash' | 'card' | 'online';

interface DeliveryState {
    method: DeliveryMethod;
    address: CustomerAddress | null;
    pickupPoint: PickupPoint | null;
    loaded: boolean;
}

export const Checkout: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { cartItems, loading: cartLoading, clearCart, fetchCartItems } = useCart();

    const [delivery, setDelivery] = useState<DeliveryState>({
        method: 'courier',
        address: null,
        pickupPoint: null,
        loaded: false,
    });
    const [paymentMethod, setPaymentMethod] = useState<PaymentMethod>('cash');
    const [notes, setNotes] = useState('');
    const [deliveryPrice, setDeliveryPrice] = useState(0);
    const [stockErrors, setStockErrors] = useState<Record<string, string>>({});
    const [submitting, setSubmitting] = useState(false);
    const [validating, setValidating] = useState(false);
    const [loadingPrice, setLoadingPrice] = useState(true);
    const [userId, setUserId] = useState<string | null>(null);
    const [userCountryCode, setUserCountryCode] = useState<string>('TJ');
    const [showAddressDialog, setShowAddressDialog] = useState(false);
    const [addresses, setAddresses] = useState<CustomerAddress[]>([]);

    const totalQuantity = cartItems.reduce((sum, item) => sum + item.quantity, 0);
    const itemsTotal = cartItems.reduce((sum, item) => {
        return sum + ((item.variant?.price || 0) * item.quantity);
    }, 0);
    const finalTotal = itemsTotal + deliveryPrice;

    // Загружаем сессию при монтировании
    useEffect(() => {
        const loadSession = async () => {
            const { data: { session } } = await supabase.auth.getSession();
            if (session?.user) {
                setUserId(session.user.id);
                setUserCountryCode(session.user.user_metadata?.country_code || 'TJ');
            }
        };
        loadSession();
    }, []);

    // Загрузка адресов
    const loadAddresses = useCallback(async () => {
        if (!userId) return;

        const { data, error } = await supabase
            .from('customer_addresses')
            .select('*')
            .eq('customer_id', userId)
            .order('is_default', { ascending: false });

        if (!error && data) {
            setAddresses(data);
        }
    }, [userId]);

    // Загрузка настроек доставки
    const loadDeliverySettings = useCallback(async () => {
        if (!userId) return;

        try {
            const { data: settings, error } = await supabase
                .from('customer_delivery_settings')
                .select('*')
                .eq('customer_id', userId)
                .maybeSingle();

            if (error) throw error;

            if (!settings) {
                setDelivery({ method: 'courier', address: null, pickupPoint: null, loaded: true });
                return;
            }

            const method: DeliveryMethod = settings.delivery_type === 'pickup' ? 'pickup' : 'courier';
            let address: CustomerAddress | null = null;
            let pickupPoint: PickupPoint | null = null;

            if (method === 'courier' && settings.address_id) {
                const { data, error: addrErr } = await supabase
                    .from('customer_addresses')
                    .select('*')
                    .eq('id', settings.address_id)
                    .maybeSingle();
                if (!addrErr && data) address = data;
            }

            if (method === 'pickup' && settings.pickup_point_id) {
                const { data, error: ppErr } = await supabase
                    .from('pickup_points')
                    .select('*')
                    .eq('id', settings.pickup_point_id)
                    .maybeSingle();
                if (!ppErr && data) pickupPoint = data;
            }

            setDelivery({ method, address, pickupPoint, loaded: true });
        } catch (err) {
            console.error('Error loading delivery settings:', err);
            toast.error('Ошибка загрузки настроек доставки');
            setDelivery(prev => ({ ...prev, loaded: true }));
        }
    }, [userId]);

    // Загрузка при монтировании
    useEffect(() => {
        if (userId) {
            loadAddresses();
            loadDeliverySettings();
        }
    }, [userId, loadAddresses, loadDeliverySettings]);

    // Обработка возврата из DeliverySelection
    useEffect(() => {
        if (location.state?.from === 'delivery-selection' && userId) {
            loadDeliverySettings();
            loadAddresses();
            window.history.replaceState({}, document.title);
        }
    }, [location.state, userId, loadDeliverySettings, loadAddresses]);

    // Расчет стоимости доставки
    useEffect(() => {
        if (!delivery.loaded) return;

        if (delivery.method === 'pickup') {
            setDeliveryPrice(0);
            setLoadingPrice(false);
            return;
        }

        setLoadingPrice(true);
        calculateDeliveryPriceFromDB(totalQuantity, userCountryCode).then(price => {
            setDeliveryPrice(price);
            setLoadingPrice(false);
        });
    }, [delivery.method, delivery.loaded, totalQuantity, userCountryCode]);

    // Подписка на изменения конфигурации доставки
    useEffect(() => {
        const unsubscribe = subscribeToDeliveryConfig((newConfig, countryCode) => {
            if (countryCode === userCountryCode && delivery.method !== 'pickup') {
                const price = calculateDeliveryPriceSync(totalQuantity, userCountryCode);
                setDeliveryPrice(price);
            }
        });
        return () => unsubscribe();
    }, [delivery.method, totalQuantity, userCountryCode]);

    // Проверка наличия товаров
    const validateStock = async (): Promise<boolean> => {
        setValidating(true);

        const errors: Record<string, string> = {};
        let hasError = false;

        for (const item of cartItems) {
            if (!item.variant) {
                errors[item.id] = 'Информация о товаре недоступна';
                hasError = true;
                continue;
            }

            try {
                const { data: variant, error } = await supabase
                    .from('product_variants')
                    .select('quantity')
                    .eq('id', item.variant_id)
                    .single();

                if (error) {
                    errors[item.id] = `Ошибка проверки "${item.product?.name}"`;
                    hasError = true;
                    continue;
                }

                const available = variant?.quantity || 0;
                if (available < item.quantity) {
                    errors[item.id] = `"${item.product?.name}" — доступно ${available} шт. (в корзине ${item.quantity})`;
                    hasError = true;
                }
            } catch {
                errors[item.id] = `Ошибка проверки "${item.product?.name}"`;
                hasError = true;
            }
        }

        setStockErrors(errors);
        setValidating(false);
        return !hasError;
    };

    // Проверка целостности корзины
    const verifyCartIntegrity = async (): Promise<boolean> => {
        if (!userId) return false;

        const { data: dbCart, error } = await supabase
            .from('customer_cart')
            .select('id, quantity')
            .eq('customer_id', userId);

        if (error || !dbCart) return false;
        if (dbCart.length !== cartItems.length) return false;

        return cartItems.every(local => {
            const db = dbCart.find(d => d.id === local.id);
            return db && db.quantity === local.quantity;
        });
    };

    // Навигация на выбор доставки
    const goToDeliverySelection = () => {
        navigate('/delivery-selection', { state: { from: 'checkout' } });
    };

    // Обработка изменения способа доставки
    const handleDeliveryMethodChange = (method: DeliveryMethod) => {
        setDelivery(prev => ({ ...prev, method }));
    };

    // Оформление заказа
    const handleSubmit = async () => {
        if (!userId) {
            toast.error('Войдите в аккаунт для оформления заказа');
            navigate('/login');
            return;
        }

        if (cartItems.length === 0) {
            toast.error('Корзина пуста');
            navigate('/cart');
            return;
        }

        let deliveryAddress = '';

        if (delivery.method === 'courier') {
            if (!delivery.address) {
                toast.error('Выберите адрес доставки');
                goToDeliverySelection();
                return;
            }
            const a = delivery.address;
            deliveryAddress = `${a.city}, ${a.street}, ${a.house}${a.apartment ? `, кв. ${a.apartment}` : ''}`;
        } else {
            if (!delivery.pickupPoint) {
                toast.error('Выберите пункт выдачи');
                goToDeliverySelection();
                return;
            }
            deliveryAddress = delivery.pickupPoint.address;
        }

        // Проверка целостности корзины
        const cartCheckId = toast.loading('Проверка корзины...');
        const cartOk = await verifyCartIntegrity();
        toast.dismiss(cartCheckId);

        if (!cartOk) {
            toast.error('Корзина была изменена. Пожалуйста, обновите страницу.');
            await fetchCartItems();
            navigate('/cart');
            return;
        }

        // Проверка наличия
        const stockCheckId = toast.loading('Проверка наличия товаров...');
        const stockOk = await validateStock();
        toast.dismiss(stockCheckId);

        if (!stockOk) {
            toast.error('Некоторые товары недоступны в выбранном количестве');
            return;
        }

        // Получаем данные пользователя
        const { data: { session } } = await supabase.auth.getSession();
        const userData = session?.user;

        if (!userData) {
            toast.error('Ошибка авторизации');
            return;
        }

        const fullName = [userData.user_metadata?.first_name, userData.user_metadata?.last_name]
            .filter(Boolean)
            .join(' ') || 'Клиент';
        const fullPhone = `${userData.user_metadata?.phone_country_code || '+992'}${userData.user_metadata?.phone_number || ''}`;

        setSubmitting(true);

        try {
            const result = await createOrder({
                delivery_address: deliveryAddress,
                customer_phone: fullPhone,
                customer_name: fullName,
                payment_method: paymentMethod,
                delivery_method: delivery.method,
                delivery_price: delivery.method === 'pickup' ? 0 : deliveryPrice,
                notes: notes || undefined,
                country_code: userCountryCode,
            });

            if (!result.success) {
                toast.error(result.message);
                return;
            }

            await clearCart();
            toast.success(`Заказ #${result.order_number} оформлен! (${result.items_count} товаров)`);
            navigate('/profile');
        } catch (error: any) {
            console.error('Checkout error:', error);
            toast.error(error.message || 'Ошибка оформления заказа');
        } finally {
            setSubmitting(false);
        }
    };

    const handleAddressAdded = (address: CustomerAddress) => {
        setAddresses(prev => [address, ...prev]);
        setDelivery(prev => ({ ...prev, address }));
        setShowAddressDialog(false);
    };

    const isLoading = cartLoading || !delivery.loaded || loadingPrice || validating;

    if (isLoading) {
        return (
            <div className="loader-container">
                <div className="spinner" />
                <p>Загрузка...</p>
            </div>
        );
    }

    if (!userId) {
        return (
            <div className="empty-state">
                <div className="empty-state-icon">👤</div>
                <h3>Войдите в аккаунт</h3>
                <p>Чтобы оформить заказ, войдите в аккаунт</p>
                <button onClick={() => navigate('/login')} className="auth-button">
                    Войти
                </button>
            </div>
        );
    }

    if (cartItems.length === 0) {
        return (
            <div className="empty-state">
                <div className="empty-state-icon">🛒</div>
                <h3>Корзина пуста</h3>
                <button onClick={() => navigate('/products')} className="auth-button">
                    Перейти в каталог
                </button>
            </div>
        );
    }

    return (
        <div className="checkout-container">
            <div className="checkout-header">
                <button onClick={() => navigate(-1)} className="back-button">
                    <ArrowLeftIcon style={{ width: 20, height: 20 }} /> Назад
                </button>
                <h1>Оформление заказа</h1>
            </div>

            <div className="checkout-grid">
                <div className="checkout-form">
                    {/* Товары */}
                    <div className="checkout-section">
                        <h2>📦 Товары ({totalQuantity} шт.)</h2>
                        <div className="checkout-items">
                            {cartItems.map(item => (
                                <div
                                    key={item.id}
                                    className={`checkout-item ${stockErrors[item.id] ? 'has-error' : ''}`}
                                >
                                    <img
                                        src={item.product?.images?.[0] || 'https://via.placeholder.com/60'}
                                        alt={item.product?.name}
                                        className="checkout-item-image"
                                    />
                                    <div className="checkout-item-info">
                                        <div className="checkout-item-name">{item.product?.name}</div>
                                        <div className="checkout-item-variant">
                                            {item.variant?.color || item.variant?.size_or_volume || 'Стандарт'}
                                        </div>
                                        <div className="checkout-item-price">
                                            {item.variant?.price.toLocaleString()} TJS × {item.quantity}
                                        </div>
                                        {stockErrors[item.id] && (
                                            <div className="error-text">{stockErrors[item.id]}</div>
                                        )}
                                    </div>
                                    <div className="checkout-item-total">
                                        {((item.variant?.price || 0) * item.quantity).toLocaleString()} TJS
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Способ доставки */}
                    <div className="checkout-section">
                        <h2>🚚 Способ доставки</h2>
                        <div className="delivery-options">
                            <label
                                className={`delivery-option ${delivery.method === 'courier' ? 'active' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="delivery"
                                    value="courier"
                                    checked={delivery.method === 'courier'}
                                    onChange={() => handleDeliveryMethodChange('courier')}
                                />
                                <TruckIcon style={{ width: 24, height: 24 }} />
                                <div>
                                    <strong>Курьером</strong>
                                    <span>Доставим по указанному адресу</span>
                                </div>
                                <div className="delivery-price">
                                    {delivery.method === 'courier'
                                        ? deliveryPrice > 0
                                            ? `${deliveryPrice.toLocaleString()} TJS`
                                            : 'Бесплатно'
                                        : ''}
                                </div>
                            </label>

                            <label
                                className={`delivery-option ${delivery.method === 'pickup' ? 'active' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="delivery"
                                    value="pickup"
                                    checked={delivery.method === 'pickup'}
                                    onChange={() => handleDeliveryMethodChange('pickup')}
                                />
                                <BuildingStorefrontIcon style={{ width: 24, height: 24 }} />
                                <div>
                                    <strong>Пункт выдачи</strong>
                                    <span>Самовывоз из пункта выдачи</span>
                                </div>
                                <div className="delivery-price">Бесплатно</div>
                            </label>
                        </div>
                    </div>

                    {/* Адрес / Пункт выдачи */}
                    <div className="checkout-section">
                        <h2>
                            <MapPinIcon style={{ width: 20, height: 20 }} />
                            {delivery.method === 'courier' ? 'Адрес доставки' : 'Пункт выдачи'}
                        </h2>

                        {delivery.method === 'courier' ? (
                            delivery.address ? (
                                <div className="selected-address">
                                    <div className="address-display">
                                        {delivery.address.city}, {delivery.address.street}, {delivery.address.house}
                                        {delivery.address.apartment && `, кв. ${delivery.address.apartment}`}
                                    </div>
                                    <button onClick={goToDeliverySelection} className="change-delivery-btn">
                                        Изменить
                                    </button>
                                </div>
                            ) : (
                                <button onClick={goToDeliverySelection} className="select-delivery-btn">
                                    Выбрать адрес
                                </button>
                            )
                        ) : delivery.pickupPoint ? (
                            <div className="selected-address">
                                <div className="address-display">
                                    <strong>{delivery.pickupPoint.name}</strong>
                                    <div>{delivery.pickupPoint.address}</div>
                                    {delivery.pickupPoint.working_hours && (
                                        <div className="working-hours">
                                            ⏰ {delivery.pickupPoint.working_hours}
                                        </div>
                                    )}
                                </div>
                                <button onClick={goToDeliverySelection} className="change-delivery-btn">
                                    Изменить
                                </button>
                            </div>
                        ) : (
                            <button onClick={goToDeliverySelection} className="select-delivery-btn">
                                Выбрать пункт выдачи
                            </button>
                        )}
                    </div>

                    {/* Способ оплаты */}
                    <div className="checkout-section">
                        <h2>
                            <CreditCardIcon style={{ width: 20, height: 20 }} /> Способ оплаты
                        </h2>
                        <div className="payment-options">
                            <label
                                className={`payment-option ${paymentMethod === 'cash' ? 'active' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="payment"
                                    value="cash"
                                    checked={paymentMethod === 'cash'}
                                    onChange={() => setPaymentMethod('cash')}
                                />
                                <span>💵 Наличными при получении</span>
                            </label>
                            <label
                                className={`payment-option ${paymentMethod === 'card' ? 'active' : ''}`}
                            >
                                <input
                                    type="radio"
                                    name="payment"
                                    value="card"
                                    checked={paymentMethod === 'card'}
                                    onChange={() => setPaymentMethod('card')}
                                />
                                <span>💳 Картой при получении</span>
                            </label>
                        </div>
                    </div>

                    {/* Комментарий */}
                    <div className="checkout-section">
                        <h2>📝 Комментарий к заказу</h2>
                        <textarea
                            rows={3}
                            value={notes}
                            onChange={e => setNotes(e.target.value)}
                            placeholder="Пожелания по доставке, удобное время..."
                            className="notes-input"
                        />
                    </div>
                </div>

                {/* Итого */}
                <div className="checkout-summary">
                    <h2>Итого</h2>
                    <div className="summary-row">
                        <span>Товары ({totalQuantity} шт.):</span>
                        <span>{itemsTotal.toLocaleString()} TJS</span>
                    </div>
                    <div className="summary-row">
                        <span>Доставка:</span>
                        <span>
                            {deliveryPrice === 0
                                ? `Бесплатно${delivery.method === 'pickup' ? ' (самовывоз)' : ''}`
                                : `${deliveryPrice.toLocaleString()} TJS`}
                        </span>
                    </div>
                    <div className="summary-total">
                        <span>Итого к оплате:</span>
                        <span>{finalTotal.toLocaleString()} TJS</span>
                    </div>

                    <button
                        onClick={handleSubmit}
                        disabled={submitting || Object.keys(stockErrors).length > 0}
                        className="checkout-button"
                    >
                        {submitting
                            ? 'Оформление...'
                            : `Оформить заказ (${finalTotal.toLocaleString()} TJS)`}
                    </button>

                    {Object.keys(stockErrors).length > 0 && (
                        <div className="stock-warning-message">
                            ⚠️ Некоторые товары недоступны. Исправьте количество.
                        </div>
                    )}

                    <p className="secure-info">
                        🔒 Безопасная оплата
                        <br />
                        Нажимая «Оформить заказ», вы соглашаетесь с условиями оферты
                    </p>
                </div>
            </div>

            {showAddressDialog && userId && (
                <AddAddressDialog
                    customerId={userId}
                    onClose={() => setShowAddressDialog(false)}
                    onAddressAdded={handleAddressAdded}
                />
            )}
        </div>
    );
};